import React, { useEffect, useState } from 'react';
import './mcp.css';
import { useNavigate } from 'react-router-dom';
import { message, Modal } from 'antd';
import { authHeader } from '../../../utils/api';
import McpHeader from '../../../components/mcp/McpHeader';

// ─── Design tokens ───────────────────────────────────────────────────────────
const FRAUNCES = 'font-display';
const MASTHEAD_GRADIENT = 'radial-gradient(140% 120% at 85% -20%, #1E3A8A 0%, #0F1729 50%)';
const GRAIN_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.04 0'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)'/%3E%3C/svg%3E";

// ─── Primitives (matching ledger.tsx) ────────────────────────────────────────
function Card3d({ children, hero, className = '', ...rest }) {
  return (
    <div
      className={`bg-paper-50 rounded-[14px] border ${hero ? 'border-brand/20' : 'border-ink/5'} shadow-[0_18px_38px_-16px_rgba(15,23,41,0.32),inset_0_1px_1px_rgba(255,255,255,0.9)] ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

function Eyebrow({ children, tone = 'muted' }) {
  const cls = tone === 'brand' ? 'text-brand' : tone === 'onDark' ? 'text-brand-300' : 'text-ink-300';
  return <div className={`text-[9px] font-bold uppercase tracking-[0.22em] ${cls}`}>{children}</div>;
}

function Grain() {
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-35" style={{ backgroundImage: `url("${GRAIN_SVG}")` }} />;
}

function SunPill({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-full bg-sun pl-4 pr-[7px] py-[7px] text-[11px] font-extrabold text-ink active:scale-[0.98] transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
    >
      {children}
      <span aria-hidden="true" className="w-[22px] h-[22px] rounded-full bg-ink text-sun flex items-center justify-center">+</span>
    </button>
  );
}

// ─── Data mapping ────────────────────────────────────────────────────────────
// Campaigns built in the wizard are stored in vehicle_campaigns (/api/vehicle-campaigns),
// not the legacy campaigns table. Map each row onto the fields this page renders.
const PLATFORM_LABELS = { meta: 'Facebook', google: 'Google Ads', both: 'Facebook + Google Ads' };

function normalizeCampaign(row) {
  const route = row.route_data || {};
  const meta = row.meta_ads_config || {};
  const google = row.google_ads_config || {};
  const publishedId = meta.facebook_campaign_id || row.google_ads_campaign_id || null;
  const daysLive = row.status === 'active' && row.created_at
    ? Math.max(0, Math.floor((Date.now() - new Date(row.created_at).getTime()) / 86_400_000))
    : 0;
  const budget = meta.ad_set_budget_amount || meta.budget_amount || google.budget || google.daily_budget || null;
  const budgetType = String(meta.ad_set_budget_type || meta.budget_type || google.budget_type || 'daily').toLowerCase();

  return {
    ...row,
    logic: row.campaign_type === 'route' ? 'route' : 'stop',
    stop_count: Array.isArray(route.stops) ? route.stops.length : 0,
    route_count: Array.isArray(route.routes) ? route.routes.length : 0,
    days_live: daysLive,
    budget,
    budgetType,
    platform: PLATFORM_LABELS[row.ad_platform] || null,
    publishedId,
  };
}

// ─── Status helpers ──────────────────────────────────────────────────────────
function isLive(c) {
  return c.status === 'live' || c.status === 'active' || c.status === 'published';
}
// 'pending' = saved but not posted to the ad platform (e.g. Facebook rejected it)
function needsAttention(c) {
  return c.status === 'in_review' || c.status === 'needs_attention' || c.status === 'pending';
}

const FILTER_CHIPS = ['all', 'live', 'attention', 'draft', 'paused', 'completed', 'stop', 'route'];

function applyFilter(list, filter) {
  if (filter === 'all') return list;
  if (filter === 'live') return list.filter(isLive);
  if (filter === 'attention') return list.filter(needsAttention);
  if (filter === 'draft') return list.filter(c => c.status === 'draft');
  if (filter === 'paused') return list.filter(c => c.status === 'paused');
  if (filter === 'completed') return list.filter(c => c.status === 'completed');
  if (filter === 'stop') return list.filter(c => c.logic === 'stop' || c.campaign_type === 'stop');
  if (filter === 'route') return list.filter(c => c.logic === 'route' || c.campaign_type === 'route');
  return list;
}

function countBy(list) {
  return {
    total: list.length,
    live: list.filter(isLive).length,
    attention: list.filter(needsAttention).length,
    paused: list.filter(c => c.status === 'paused').length,
    completed: list.filter(c => c.status === 'completed').length,
    drafts: list.filter(c => c.status === 'draft').length,
  };
}

function chipLabel(c, counts) {
  switch (c) {
    case 'all': return `All ${counts.total}`;
    case 'live': return `Live ${counts.live}`;
    case 'attention': return `Needs review ${counts.attention}`;
    case 'paused': return `Paused ${counts.paused}`;
    case 'completed': return `Completed ${counts.completed}`;
    case 'draft': return `Drafts ${counts.drafts}`;
    case 'stop': return 'StopLogic';
    case 'route': return 'RouteLogic';
    default: return c;
  }
}

function statusMicroLabel(status) {
  if (isLive({ status })) return { text: '● LIVE', className: 'text-sage' };
  if (status === 'completed') return { text: '● COMPLETED', className: 'text-ink-300' };
  if (status === 'in_review') return { text: '● IN REVIEW', className: 'text-sun-600' };
  if (status === 'needs_attention') return { text: '● NEEDS ATTENTION', className: 'text-ember' };
  if (status === 'pending') return { text: '● NOT PUBLISHED', className: 'text-ember' };
  if (status === 'paused') return { text: '● PAUSED', className: 'text-ink-300' };
  return { text: 'DRAFT', className: 'text-sun-600' };
}

function logicTagClass(logic) {
  return logic === 'route' ? 'bg-brand-100 text-brand-dark' : 'bg-sun-100 text-sun-600';
}

function subLine(c) {
  if (c.status === 'draft') return 'Draft — not published';
  const source = c.logic === 'route'
    ? `${c.route_count || 0} route${c.route_count === 1 ? '' : 's'}`
    : `${c.pinCount || c.stop_count || 0} stops`;
  if (c.status === 'pending') return `${source} · not posted to ${c.platform || 'ad platform'}`;
  const days = c.daysLive || c.days_live || 0;
  return `${source} · ${days}d live`;
}

function fmtNumber(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

function fmtShortDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ─── Portfolio masthead ─────────────────────────────────────────────────────
function PortfolioMasthead({ campaigns, loading }) {
  const totalSpend = campaigns.reduce((s, c) => s + Number(c.spendUsd || c.budget || 0), 0);
  const totalImpressions = campaigns.reduce((s, c) => s + Number(c.impressions || 0), 0);
  const totalClicks = campaigns.reduce((s, c) => s + Number(c.linkClicks || 0), 0);
  const totalReach = campaigns.reduce((s, c) => s + Number(c.reach || 0), 0);
  const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
  const cpc = totalClicks > 0 ? (totalSpend / totalClicks) : 0;

  const cards = [
    { key: 'ctr', label: 'Click-through', hero: true, value: ctr.toFixed(2), suffix: '%', cls: 'text-[28px] text-brand' },
    { key: 'clicks', label: 'Link clicks', value: fmtNumber(totalClicks), cls: 'text-[20px] text-ink' },
    { key: 'spent', label: 'Spent', value: `$${totalSpend.toFixed(2)}`, cls: 'text-[20px] text-ink' },
    { key: 'impr', label: 'Impressions', value: fmtNumber(totalImpressions), cls: 'text-[20px] text-ink' },
    { key: 'cpc', label: 'CPC', value: `$${cpc.toFixed(2)}`, cls: 'text-[20px] text-ink' },
  ];

  return (
    <div>
      <div className="relative rounded-[14px] px-6 pt-5 pb-[50px]" style={{ background: MASTHEAD_GRADIENT }}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <Eyebrow tone="onDark">Portfolio · lifetime</Eyebrow>
            <div className={`${FRAUNCES} mt-1.5 text-[15px] font-medium text-paper-50`}>
              {loading ? '—' : `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`}
            </div>
          </div>
        </div>
      </div>

      <div className="-mb-7 -translate-y-7 px-6">
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-[1.2fr_.85fr_.85fr_.85fr_.85fr]">
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <Card3d key={i} hero={i === 0} className="space-y-2 p-3.5">
                  <div className="h-2 w-14 animate-pulse rounded-full bg-ink/10" />
                  <div className="h-6 w-16 animate-pulse rounded-full bg-ink/10" />
                </Card3d>
              ))
            : cards.map((card, i) => (
                <div key={card.key} className="animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                  <Card3d hero={card.hero} className="p-3.5">
                    <Eyebrow tone={card.hero ? 'brand' : 'muted'}>{card.label}</Eyebrow>
                    <div className={`${FRAUNCES} mt-1 font-bold leading-none ${card.cls}`}>
                      {card.value}
                      {card.suffix && <span className="text-[14px]">{card.suffix}</span>}
                    </div>
                  </Card3d>
                </div>
              ))}
        </div>
        {!loading && (
          <p className="mt-1.5 text-[9.5px] font-medium text-ink-300">
            {totalReach > 0
              ? `Reach ${fmtNumber(totalReach)} (combined — may overlap)`
              : 'Reach — (combined — may overlap)'}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Invitation pane (default right pane) ────────────────────────────────────
function InvitationPane() {
  return (
    <Card3d className="relative flex min-h-[420px] flex-col items-center justify-center overflow-hidden p-10 text-center">
      <Grain />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full opacity-40 blur-3xl"
        style={{ background: 'radial-gradient(circle, rgba(31,79,229,0.35) 0%, rgba(31,79,229,0) 70%)' }}
      />
      <div className="relative">
        <div
          aria-hidden="true"
          className="mx-auto h-12 w-12 -rotate-45 rounded-[50%_50%_50%_0] shadow-[0_12px_28px_-8px_rgba(31,79,229,0.55)]"
          style={{ background: 'linear-gradient(120deg,#1F4FE5,#29C5FF)' }}
        />
        <h3 className={`${FRAUNCES} mt-6 text-xl font-bold text-ink`}>Select a campaign</h3>
        <p className="mt-2 text-sm text-ink-300">Performance, variations & controls appear here</p>
      </div>
    </Card3d>
  );
}

// ─── Campaign detail pane ────────────────────────────────────────────────────
function CampaignDetailPane({ campaign, onEdit, onPublish, onPause, onResume, onDelete, busyAction }) {
  if (!campaign) {
    return (
      <div className="rounded-3xl border-2 border-dashed border-paper-300 bg-paper-50 p-8 text-center">
        <p className="text-sm text-ink-100">Campaign not found — pick one from the list.</p>
      </div>
    );
  }

  const c = campaign;
  const isDraft = c.status === 'draft' || !c.publishedId;
  const logicLabel = c.logic === 'route' ? 'RouteLogic' : 'StopLogic';
  const eyebrowText = isDraft ? 'DRAFT' : `${logicLabel}`;
  const pins = c.pinCount || c.stop_count || 0;
  const daysLive = c.daysLive || c.days_live || 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-hidden rounded-2xl shadow-[0_28px_60px_-24px_rgba(15,23,41,0.35)]">
        {/* Dark masthead */}
        <div className={`relative ${isDraft ? 'px-6 py-5' : 'px-6 pt-5 pb-[50px]'}`} style={{ background: MASTHEAD_GRADIENT }}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <Eyebrow tone="onDark">{eyebrowText}</Eyebrow>
              <h2 className={`${FRAUNCES} mt-1.5 truncate text-[22px] font-bold leading-tight text-paper-50`}>
                {c.name || c.campaign_name || 'Untitled audience'}
              </h2>
              <p className="mt-1.5 text-xs text-paper-50/60">
                {pins} pins{c.radiusMi ? ` @ ${c.radiusMi}mi` : ''}{daysLive > 0 ? ` · ${daysLive}d live` : ''}
              </p>
            </div>
            {!isDraft && (
              <div className="flex flex-wrap items-center gap-2">
                {c.status !== 'paused' && c.status !== 'completed' && (
                  <button
                    type="button"
                    onClick={() => onPause?.(c)}
                    disabled={!!busyAction}
                    className="rounded-full border border-paper-50/30 bg-paper-50/[0.08] px-4 py-[7px] text-[11px] font-bold text-paper-50 hover:bg-paper-50/20 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busyAction === 'pause' ? 'Pausing…' : 'Pause'}
                  </button>
                )}
                {c.status === 'paused' && (
                  <button
                    type="button"
                    onClick={() => onResume?.(c)}
                    disabled={!!busyAction}
                    className="rounded-full border border-paper-50/30 bg-paper-50/[0.08] px-4 py-[7px] text-[11px] font-bold text-paper-50 hover:bg-paper-50/20 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {busyAction === 'resume' ? 'Resuming…' : 'Resume'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Paper body with seam cards */}
        <div className="relative bg-paper-50 px-6 pb-6">
          <Grain />
          <div className="relative z-10 space-y-4">
            {!isDraft && (
              <div className="-mb-4 -translate-y-7">
                <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-[1.25fr_1fr_1fr_1fr]">
                  {[
                    { label: 'Click-through', value: c.linkCtrPct ? c.linkCtrPct.toFixed(2) : '—', suffix: '%', hero: true, cls: 'text-[28px] text-brand' },
                    { label: 'Link clicks', value: c.linkClicks != null ? fmtNumber(c.linkClicks) : '—', cls: 'text-[20px] text-ink' },
                    { label: 'Spent', value: c.spendUsd != null ? `$${c.spendUsd.toFixed(2)}` : '—', cls: 'text-[20px] text-ink' },
                    { label: 'Impressions', value: c.impressions != null ? fmtNumber(c.impressions) : '—', cls: 'text-[20px] text-ink' },
                  ].map((card, i) => (
                    <div key={card.label} className="animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                      <Card3d hero={card.hero} className="p-3.5">
                        <Eyebrow tone={card.hero ? 'brand' : 'muted'}>{card.label}</Eyebrow>
                        <div className={`${FRAUNCES} mt-1 font-bold leading-none ${card.cls}`}>
                          {card.value}{card.suffix && card.value !== '—' && <span className="text-[14px]">{card.suffix}</span>}
                        </div>
                      </Card3d>
                    </div>
                  ))}
                </div>
                <p className="mt-1.5 text-[9.5px] font-medium text-ink-300">
                  {c.reach != null
                    ? `Reach ${fmtNumber(c.reach)} · CPC ${c.costPerLinkClickUsd ? `$${c.costPerLinkClickUsd.toFixed(2)}` : '—'} · CPM ${c.cpmUsd ? `$${c.cpmUsd.toFixed(2)}` : '—'}`
                    : 'No data yet — metrics appear once the campaign starts serving.'}
                </p>
              </div>
            )}

            {/* Setup grid */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-ink/[0.08] pt-3.5 lg:grid-cols-4">
              <div>
                <Eyebrow>Sources</Eyebrow>
                <div className="mt-1 truncate text-[11.5px] font-bold text-ink">
                  {c.logic === 'route' ? `${c.route_count || 0} routes` : `${c.sourceIds?.length || c.stop_count || 0} stops`}
                </div>
              </div>
              <div>
                <Eyebrow>Budget</Eyebrow>
                <div className="mt-1 truncate text-[11.5px] font-bold text-ink">
                  {c.budget ? `$${Number(c.budget).toFixed(0)}${c.budgetType === 'lifetime' ? ' lifetime' : '/day'}` : '—'}
                </div>
              </div>
              <div>
                <Eyebrow>Schedule</Eyebrow>
                <div className="mt-1 truncate text-[11.5px] font-bold text-ink">
                  {c.start_date ? `${fmtShortDate(c.start_date)} → ${c.end_date ? fmtShortDate(c.end_date) : 'Ongoing'}` : '—'}
                </div>
              </div>
              <div>
                <Eyebrow>Platform</Eyebrow>
                <div className="mt-1 truncate text-[11.5px] font-bold text-ink">{c.platform || 'Facebook'}</div>
              </div>
            </div>

            {/* Publishing footer */}
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-t border-ink/[0.08] pt-3.5">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
                <Eyebrow>Publishing</Eyebrow>
                <span className="text-[10.5px] font-bold text-ink-300">
                  {c.publishedId ? `${c.platform || 'Ad'} campaign ${c.publishedId}` : 'Not published'}
                </span>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => onEdit?.(c)} className="text-[10.5px] font-bold text-ink-300 hover:text-ink">Edit</button>
                {isDraft && (
                  <button type="button" onClick={() => onPublish?.(c)} className="text-[10.5px] font-bold text-brand hover:text-brand-dark">
                    Publish →
                  </button>
                )}
                {/* Drafts / failed posts can be deleted too, so they don't clutter the list */}
                <button
                  type="button"
                  onClick={() => onDelete?.(c)}
                  disabled={!!busyAction}
                  className="text-[10.5px] font-bold text-ember hover:text-ember-dark disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busyAction === 'delete' ? 'Deleting…' : 'Delete campaign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
const McpAudiences = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    // The endpoint defaults to 10 rows, so ask for enough to cover the whole portfolio
    fetch('/api/vehicle-campaigns?limit=500', { headers: authHeader() })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        const list = Array.isArray(data) ? data : data.items || data.campaigns || data.data || [];
        // Audiences are fleet-built ad campaigns (StopLogic / RouteLogic); proximity SMS/email campaigns are not listed
        setCampaigns(list.filter(c => c.campaign_type === 'stop' || c.campaign_type === 'route').map(normalizeCampaign));
        setLoading(false);
      })
      .catch(() => { setError('Could not load audiences.'); setLoading(false); });
  }, []);

  // Which action is in flight on the selected campaign ('pause' | 'resume' | 'delete')
  const [busyAction, setBusyAction] = useState(null);

  // The backend mirrors pause/resume/delete to Meta and Google Ads and reports
  // per-platform results in platform_sync — surface any that failed.
  const warnPlatformFailures = (platformSync, actionLabel) => {
    const failures = (platformSync || []).filter(r => r.applicable && !r.success);
    failures.forEach(f => {
      const platform = f.platform === 'google' ? 'Google Ads' : 'Facebook';
      message.warning(`${actionLabel} in Pathmarkr, but ${platform} could not be updated: ${f.error || 'unknown error'}`, 8);
    });
    return failures.length === 0;
  };

  const handleStatusChange = async (campaign, status) => {
    const action = status === 'paused' ? 'pause' : 'resume';
    setBusyAction(action);
    try {
      const res = await fetch(`/api/vehicle-campaigns/${campaign.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ status }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      const { platform_sync: platformSync, ...updated } = data;
      setCampaigns(prev => prev.map(c => (c.id === campaign.id ? normalizeCampaign(updated) : c)));
      const label = status === 'paused' ? 'Paused' : 'Resumed';
      if (warnPlatformFailures(platformSync, label)) {
        message.success(`${label} "${campaign.name}"${campaign.platform ? ` on ${campaign.platform}` : ''}`);
      }
    } catch (e) {
      message.error(`Could not ${action} campaign: ${e.message}`);
    } finally {
      setBusyAction(null);
    }
  };

  const handleDelete = (campaign) => {
    Modal.confirm({
      title: `Delete "${campaign.name}"?`,
      content: campaign.publishedId
        ? `This also deletes the live campaign on ${campaign.platform || 'the ad platform'}. This cannot be undone.`
        : 'This cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusyAction('delete');
        try {
          const res = await fetch(`/api/vehicle-campaigns/${campaign.id}`, {
            method: 'DELETE',
            headers: authHeader(),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

          setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
          setSelectedKey(null);
          if (warnPlatformFailures(data.platform_sync, 'Deleted')) {
            message.success(`Deleted "${campaign.name}"`);
          }
        } catch (e) {
          message.error(`Could not delete campaign: ${e.message}`);
        } finally {
          setBusyAction(null);
        }
      },
    });
  };

  const counts = countBy(campaigns);
  const rows = applyFilter(campaigns, filter);
  const selected = campaigns.find(c => c.id === selectedKey);
  const publishedCount = campaigns.filter(c => c.publishedId || c.status === 'active').length;

  return (
    <div className="mcp-page" style={{ padding: 0 }}>
      <McpHeader title="Audiences" />

      <div className="mx-auto w-full max-w-7xl p-6 md:p-8">
        {/* Page header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>Your fleet, advertising</Eyebrow>
            <h2 className={`${FRAUNCES} mt-1 text-[22px] font-bold text-ink`}>Audiences</h2>
            <p className="mt-1.5 text-sm text-ink-200">
              Every audience you've built from your fleet data — drafts here, published campaigns from your account. {loading ? '—' : `${campaigns.length} total`}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <SunPill onClick={() => navigate('/client/mcp/build')}>New campaign</SunPill>
          </div>
        </div>

        {error && (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-ember/25 bg-ember/5 px-4 py-2.5 text-[12px] font-semibold text-ember">
            <span>{error}</span>
          </div>
        )}

        {/* Portfolio masthead */}
        {publishedCount > 0 && !loading && (
          <div className="mt-6">
            <PortfolioMasthead campaigns={campaigns} loading={loading} />
          </div>
        )}

        {loading ? (
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map(i => (
              <div key={i} className="card animate-pulse">
                <div className="h-5 w-2/5 rounded-full bg-paper-300" />
                <div className="mt-4 h-6 w-3/4 rounded-full bg-paper-300" />
                <div className="mt-3 h-4 w-full rounded-full bg-paper-200" />
                <div className="mt-4 h-24 rounded-2xl bg-paper-200" />
              </div>
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <Card3d className="relative mt-12 overflow-hidden p-12 text-center">
            <Grain />
            <div className="relative">
              <h3 className={`${FRAUNCES} text-xl font-bold text-ink`}>You haven't built any audiences yet</h3>
              <p className="mt-2 text-sm text-ink-100">
                Open the map and select stops or routes to build your first campaign.
                Campaigns you publish from the builder show up here automatically.
              </p>
              <div className="mt-5 flex justify-center">
                <SunPill onClick={() => navigate('/client/mcp/map')}>Open the map</SunPill>
              </div>
            </div>
          </Card3d>
        ) : (
          <div className="mt-6 flex flex-col gap-4 lg:flex-row">
            {/* List pane */}
            <div className="lg:w-1/3 lg:min-w-[300px]">
              <div className="flex h-full flex-col">
                {/* Filter chips */}
                <div className="flex flex-wrap items-center gap-2 p-3">
                  {FILTER_CHIPS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setFilter(c)}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-bold capitalize transition-colors ${
                        filter === c
                          ? 'bg-ink text-paper-50'
                          : 'border border-ink/[0.08] bg-paper-50 text-ink-200 hover:border-ink/20 hover:text-ink'
                      }`}
                    >
                      {chipLabel(c, counts)}
                    </button>
                  ))}
                </div>

                {/* Rows */}
                <div className="flex-1 space-y-1.5 overflow-y-auto p-3 pt-0">
                  {rows.length === 0 && (
                    <Card3d className="relative overflow-hidden p-4">
                      <Grain />
                      <p className="relative text-sm text-ink-300">Nothing matches this filter.</p>
                    </Card3d>
                  )}
                  {rows.map(c => {
                    const isSelected = c.id === selectedKey;
                    const micro = statusMicroLabel(c.status);
                    const logic = c.logic || c.campaign_type || 'stop';
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setSelectedKey(isSelected ? null : c.id)}
                        className="block w-full text-left transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-safe:hover:-translate-y-0.5"
                      >
                        <Card3d
                          hero={isSelected}
                          className={`flex items-center gap-2.5 px-3 py-2.5 hover:shadow-[0_22px_44px_-16px_rgba(15,23,41,0.38),inset_0_1px_1px_rgba(255,255,255,0.9)]${isSelected ? ' !border-brand !bg-brand-50' : ''}`}
                        >
                          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${logicTagClass(logic)}`}>
                            {logic === 'route' ? 'RouteLogic' : 'StopLogic'}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[13px] font-extrabold text-ink">{c.name || c.campaign_name || 'Untitled'}</div>
                            <div className="truncate text-[10.5px] text-ink-300">{subLine(c)}</div>
                          </div>
                          <div className="ml-auto shrink-0 text-right">
                            <div className="text-xs font-extrabold tabular-nums text-ink-300">—</div>
                            <div className={`text-[8.5px] font-bold tracking-[0.06em] ${micro.className}`}>{micro.text}</div>
                          </div>
                        </Card3d>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Detail pane */}
            <div className="hidden flex-1 lg:block">
              <div className="animate-fade-up" key={selected?.id ?? 'invite'}>
                {!selected ? (
                  <InvitationPane />
                ) : (
                  <CampaignDetailPane
                    campaign={selected}
                    onEdit={(c) => navigate(`/client/campaigns/edit/${c.id}`)}
                    onPublish={(c) => navigate('/client/mcp/build')}
                    onPause={(c) => handleStatusChange(c, 'paused')}
                    onResume={(c) => handleStatusChange(c, 'active')}
                    onDelete={handleDelete}
                    busyAction={busyAction}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default McpAudiences;
